import { SeverityLevel } from '@microsoft/applicationinsights-web';
import {
  CreateThirdPartyInvoiceHeaderRequest,
  DeleteThirdPartyInvoiceDetailRequest,
  DeleteThirdPartyInvoiceHeaderRequest,
  GetThirdPartyInvoiceHeadersRequest,
  QuickAddThirdPartyInvoiceDetailRequest,
  ThirdPartyInvoiceDetail,
  ThirdPartyInvoiceHeader,
  UpdateThirdPartyInvoiceDetailsRequest,
  UpdateThirdPartyInvoiceHeaderRequest,
} from '../../api/models/third-party-invoice.models';
import ThirdPartyInvoiceService from '../../api/services/third-party-invoice.service';
import { useAppInsightsLogger } from '../../logging/index';
import { Resolution, UserActivityAction, UserActivityActionSummary, UserActivityPageName } from '../../models';
import { globalSlice, logUserActivity } from '../common/index';
import { AppDispatch, AppThunk, RootState } from '../store';
import { thirdPartyInvoiceSlice } from './third-party-invoice.slice';
import { getVendor } from './vendor-management.thunks';
import { vendorProductSlice } from './vendor-product.slice';

const appInsightsLogger = useAppInsightsLogger();
const thirdPartyInvoiceService = ThirdPartyInvoiceService.getInstance();

export const resetThirdPartyInvoiceState = (): AppThunk => async (dispatch: AppDispatch) => {
  dispatch(thirdPartyInvoiceSlice.actions.resetState());
  dispatch(vendorProductSlice.actions.resetVendorCustomList());
};

export const getThirdPartyInvoiceHeaders =
  (request: GetThirdPartyInvoiceHeadersRequest): AppThunk =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (!getState().thirdPartyInvoice.isInvoiceHeadersLoading) {
        dispatch(thirdPartyInvoiceSlice.actions.setIsInvoiceHeadersLoading(true));
      }

      const { data } = await thirdPartyInvoiceService.getThirdPartyInvoiceHeaders(request);

      if (data.IsSuccess) {
        dispatch(thirdPartyInvoiceSlice.actions.setInvoiceHeaders(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    } finally {
      dispatch(thirdPartyInvoiceSlice.actions.setIsInvoiceHeadersLoading(false));
    }
  };

export const getThirdPartyInvoiceHeader =
  (thirdPartyInvoiceHeaderId: string, success?: (data: ThirdPartyInvoiceHeader) => void): AppThunk =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (!getState().thirdPartyInvoice.isInvoiceHeadersLoading) {
        dispatch(thirdPartyInvoiceSlice.actions.setIsInvoiceHeadersLoading(true));
      }

      const { data } = await thirdPartyInvoiceService.getThirdPartyInvoiceHeader(thirdPartyInvoiceHeaderId);

      if (data.IsSuccess) {
        success?.(data.ResultObject);
        dispatch(thirdPartyInvoiceSlice.actions.setInvoiceHeader(data.ResultObject));

        if (
          data.ResultObject.ThirdPartyVendor.ThirdPartyVendorId !==
          getState().vendorManagement.vendor?.ThirdPartyVendorId
        ) {
          await dispatch(
            getVendor(data.ResultObject.CustomerId, data.ResultObject.ThirdPartyVendor.ThirdPartyVendorId)
          );
        }
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    } finally {
      dispatch(thirdPartyInvoiceSlice.actions.setIsInvoiceHeadersLoading(false));
    }
  };

export const createThirdPartyInvoiceHeader =
  (request: CreateThirdPartyInvoiceHeaderRequest, resolution: Resolution): AppThunk<Promise<string>> =>
  async (dispatch: AppDispatch): Promise<string> => {
    let invoiceHeaderId = '';

    try {
      dispatch(
        logUserActivity({
          action: UserActivityAction.CreateInvoiceHeader,
          pageName: UserActivityPageName.ThirdPartyInvoices,
          actionSummary: UserActivityActionSummary.CreateThirdPartyInvioce,
          resolution: resolution,
        })
      );

      const { data } = await thirdPartyInvoiceService.createThirdPartyInvoiceHeader(request);

      if (data.IsSuccess) {
        dispatch(thirdPartyInvoiceSlice.actions.setInvoiceHeader(data.ResultObject));
        invoiceHeaderId = data.ResultObject.ThirdPartyInvoiceHeaderId;
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }

    return invoiceHeaderId;
  };

export const updateThirdPartyInvoiceHeader =
  (
    request: UpdateThirdPartyInvoiceHeaderRequest,
    resolution: Resolution,
    onSuccess?: () => void
  ): AppThunk<Promise<string>> =>
  async (dispatch: AppDispatch): Promise<string> => {
    let resultId = '';

    try {
      dispatch(
        logUserActivity({
          action: UserActivityAction.UpdateInvoiceHeader,
          pageName: UserActivityPageName.ThirdPartyInvoices,
          actionSummary: UserActivityActionSummary.UpdateThirdPartyInvoice,
          resolution: resolution,
        })
      );

      const { data } = await thirdPartyInvoiceService.updateThirdPartyInvoiceHeader(request);

      if (data.IsSuccess) {
        resultId = request.ThirdPartyInvoiceHeaderId;
        dispatch(thirdPartyInvoiceSlice.actions.setInvoiceHeader(data.ResultObject));
        onSuccess?.();
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }

    return resultId;
  };

export const deleteThirdPartyInvoiceHeader =
  (
    ThirdPartyInvoiceHeaderId: string,
    resolution: Resolution,
    successCallback?: () => void,
    errorCallback?: (errors: string[]) => void
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      const request: DeleteThirdPartyInvoiceHeaderRequest = {
        ThirdPartyInvoiceHeaderId: ThirdPartyInvoiceHeaderId,
      };

      dispatch(
        logUserActivity({
          action: UserActivityAction.DeleteInvoiceHeader,
          pageName: UserActivityPageName.ThirdPartyInvoices,
          actionSummary: UserActivityActionSummary.DeletedThirdPartyInvoice,
          resolution: resolution,
        })
      );

      const { data } = await thirdPartyInvoiceService.deleteThirdPartyInvoiceHeader(request);

      if (data.IsSuccess) {
        dispatch(thirdPartyInvoiceSlice.actions.removeInvoiceHeader(ThirdPartyInvoiceHeaderId));
        successCallback?.();
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
        errorCallback?.(data.ErrorMessages);
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }
  };

export const getThirdPartyInvoiceDetails =
  (thirdPartyInvoiceHeaderId: string, resolution: Resolution): AppThunk =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (!getState().thirdPartyInvoice.isInvoiceDetailsLoading) {
        dispatch(thirdPartyInvoiceSlice.actions.setIsInvoiceDetailsLoading(true));
      }

      dispatch(
        logUserActivity({
          action: UserActivityAction.GetThirdPartyInvoiceDetils,
          pageName: UserActivityPageName.ThirdPartyInvoices,
          actionSummary: UserActivityActionSummary.GetThirdPartyInvoiceDetails,
          resolution: resolution,
        })
      );

      const { data } = await thirdPartyInvoiceService.getThirdPartyInvoiceDetails(thirdPartyInvoiceHeaderId);

      if (data.IsSuccess) {
        dispatch(thirdPartyInvoiceSlice.actions.setInvoiceDetails(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    } finally {
      dispatch(thirdPartyInvoiceSlice.actions.setIsInvoiceDetailsLoading(false));
    }
  };

export const deleteThirdPartyInvoiceDetail =
  (
    request: DeleteThirdPartyInvoiceDetailRequest,
    resolution: Resolution,
    successCallback?: () => void,
    errorCallback?: (errors: string[]) => void
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(
        logUserActivity({
          action: UserActivityAction.DeleteInvoiceDetail,
          pageName: UserActivityPageName.ThirdPartyInvoiceDetail,
          actionSummary: UserActivityActionSummary.DeletedThirdPartyInvoiceDetail,
          resolution: resolution,
        })
      );

      const { data } = await thirdPartyInvoiceService.deleteThirdPartyInvoiceDetail(request);

      if (data.IsSuccess) {
        dispatch(thirdPartyInvoiceSlice.actions.removeInvoiceDetail(request.ThirdPartyInvoiceDetailId));
        successCallback?.();
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
        errorCallback?.(data.ErrorMessages);
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }
  };

export const quickAddThirdPartyInvoiceDetail =
  (
    request: QuickAddThirdPartyInvoiceDetailRequest,
    resolution: Resolution,
    success?: (data: ThirdPartyInvoiceDetail[]) => void,
    errorCallback?: (errors: string[]) => void
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(
        logUserActivity({
          action: UserActivityAction.QuickAddThirdparty,
          pageName: UserActivityPageName.QuickAddThirdPartyInvoice,
          actionSummary: UserActivityActionSummary.QuickAddThirdPartyInvoice,
          resolution: resolution,
        })
      );

      const { data } = await thirdPartyInvoiceService.quickAddThirdPartyInvoiceDetail(request);

      if (data.IsSuccess) {
        // If more than one product is returned (duplicate product #s) dont add any data to the grid
        if (data.ResultObject.length > 1) {
          success?.(data.ResultObject);
          return;
        }

        success?.(data.ResultObject);
        dispatch(thirdPartyInvoiceSlice.actions.updateInvoiceDetails(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
        errorCallback?.(data.ErrorMessages);
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }
  };

export const updateThirdPartyInvoiceDetails =
  (
    request: UpdateThirdPartyInvoiceDetailsRequest,
    resolution: Resolution,
    successCallback?: () => void | Promise<void>,
    errorCallback?: (errors: string[]) => void
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(
        logUserActivity({
          action: UserActivityAction.UpdateInvoiceDetails,
          pageName: UserActivityPageName.ThirdPartyInvoices,
          actionSummary: UserActivityActionSummary.UpdateInvoiceDetails,
          resolution: resolution,
        })
      );

      const { data } = await thirdPartyInvoiceService.updateThirdPartyInvoiceDetails(request);

      if (data.IsSuccess) {
        successCallback?.();
        dispatch(getThirdPartyInvoiceDetails(request.ThirdPartyInvoiceHeaderId, resolution));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
        errorCallback?.(data.ErrorMessages);
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }
  };
